/**
 * Function Signature Definitions
 *
 * Parameter names and descriptions for all formula functions.
 * Used by the SignatureHelpProvider to show parameter hints
 * when the user types inside function calls (e.g., SUM(|)).
 */

export interface FunctionParameter {
  name: string;
  description?: string;
  optional?: boolean;
}

export interface FunctionSignature {
  parameters: FunctionParameter[];
  /** True for variadic functions (arity '1+') */
  variadic?: boolean;
}

/**
 * Signature map for all formula functions.
 * Only includes functions that use call syntax FUNC(...).
 */
export const FUNCTION_SIGNATURES: Record<string, FunctionSignature> = {
  // Logical
  AND: { parameters: [{ name: 'value1' }, { name: 'value2', optional: true }], variadic: true },
  OR: { parameters: [{ name: 'value1' }, { name: 'value2', optional: true }], variadic: true },
  NOT: { parameters: [{ name: 'value' }] },

  // Math
  SUM: { parameters: [{ name: 'number1' }, { name: 'number2', optional: true }], variadic: true },
  AVERAGE: { parameters: [{ name: 'number1' }, { name: 'number2', optional: true }], variadic: true },
  MIN: { parameters: [{ name: 'number1' }, { name: 'number2', optional: true }], variadic: true },
  MAX: { parameters: [{ name: 'number1' }, { name: 'number2', optional: true }], variadic: true },
  ROUND: { parameters: [{ name: 'number' }, { name: 'decimals' }] },
  ABS: { parameters: [{ name: 'number' }] },
  CEIL: { parameters: [{ name: 'number' }] },
  FLOOR: { parameters: [{ name: 'number' }] },
  SQRT: { parameters: [{ name: 'number' }] },
  POWER: { parameters: [{ name: 'base' }, { name: 'exponent' }] },
  TRUNC: { parameters: [{ name: 'number' }, { name: 'decimals' }] },
  LOG: { parameters: [{ name: 'number' }] },
  LN: { parameters: [{ name: 'number' }] },
  SIGN: { parameters: [{ name: 'number' }] },

  // Text
  CONCAT: { parameters: [{ name: 'text1' }, { name: 'text2', optional: true }], variadic: true },
  UPPER: { parameters: [{ name: 'text' }] },
  LOWER: { parameters: [{ name: 'text' }] },
  LEFT: { parameters: [{ name: 'text' }, { name: 'count' }] },
  RIGHT: { parameters: [{ name: 'text' }, { name: 'count' }] },
  MID: { parameters: [{ name: 'text' }, { name: 'start' }, { name: 'length' }] },
  LEN: { parameters: [{ name: 'text' }] },
  TRIM: { parameters: [{ name: 'text' }] },
  REPLACE: { parameters: [{ name: 'text' }, { name: 'old_text' }, { name: 'new_text' }] },
  FIND: { parameters: [{ name: 'search_text' }, { name: 'text' }] },
  SEARCH: { parameters: [{ name: 'search_text' }, { name: 'text' }] },
  PROPER: { parameters: [{ name: 'text' }] },
  REPEAT: { parameters: [{ name: 'text' }, { name: 'count' }] },
  TEXT: { parameters: [{ name: 'value' }] },

  // Test
  IF: { parameters: [{ name: 'condition' }, { name: 'value_if_true' }, { name: 'value_if_false' }] },
  ISBLANK: { parameters: [{ name: 'value' }] },
  ISNUMBER: { parameters: [{ name: 'value' }] },
  ISTEXT: { parameters: [{ name: 'value' }] },
  COALESCE: { parameters: [{ name: 'value1' }, { name: 'value2', optional: true }], variadic: true },
  IFERROR: { parameters: [{ name: 'value' }, { name: 'fallback' }] },
  SWITCH: { parameters: [{ name: 'expression' }, { name: 'case1' }, { name: 'value1' }, { name: 'default', optional: true }], variadic: true },
  VALUE: { parameters: [{ name: 'text' }] },

  // Date
  TODAY: { parameters: [] },
  NOW: { parameters: [] },
  YEAR: { parameters: [{ name: 'date' }] },
  MONTH: { parameters: [{ name: 'date' }] },
  DAY: { parameters: [{ name: 'date' }] },
  HOUR: { parameters: [{ name: 'datetime' }] },
  MINUTE: { parameters: [{ name: 'datetime' }] },
  SECOND: { parameters: [{ name: 'datetime' }] },
  WEEKDAY: { parameters: [{ name: 'date' }] },
  QUARTER: { parameters: [{ name: 'date' }] },
  DATEADD: { parameters: [{ name: 'unit', description: "'day', 'month', 'year', 'hour', 'minute'" }, { name: 'amount' }, { name: 'date' }] },
  DATEDIFF: { parameters: [{ name: 'unit', description: "'day', 'month', 'year', 'hour', 'minute'" }, { name: 'date1' }, { name: 'date2' }] },
};

/**
 * Build a human-readable signature label for a function.
 * E.g., "ROUND(number, decimals)" or "SUM(number1, [number2], ...)"
 */
export function buildSignatureLabel(funcName: string, sig: FunctionSignature): string {
  const params = sig.parameters.map((p) =>
    p.optional ? `[${p.name}]` : p.name
  );
  if (sig.variadic) params.push('...');
  return `${funcName}(${params.join(', ')})`;
}
